import React from "react";
import Typography from "@mui/material/Typography";
import { MobileDatePicker } from "@mui/x-date-pickers";
import TextField from "@mui/material/TextField";
import { useApi } from "common/utils/api";
import { LoadingButton } from "common/components/LoadingButton";
import { useSnackbarAlerts } from "../../common/Snackbar";
import { useMatomo } from "@datapunt/matomo-tracker-react";
import Grid from "@mui/material/Grid";
import { isoFormatLocalDate } from "common/utils/time";
import { DateOrDateTimeRangeSelectionContext } from "common/components/DateOrDateTimeRangeSelectionContext";
import { Autocomplete } from "@mui/lab";
import {
  getUsersToSelectFromTeamSelection,
  unselectAndGetAllTeams
} from "../store/reducers/team";
import Modal, { modalStyles } from "../../common/Modal";
import { TeamFilter } from "../components/TeamFilter";
import { EmployeeFilter } from "../components/EmployeeFilter";
import Notice from "../../common/Notice";
import { HTTP_QUERIES } from "common/utils/apiQueries/httpQueries";
import { useExportsContext } from "../utils/contextExports";
import { Checkbox } from "@codegouvfr/react-dsfr/Checkbox";
import { validateExportParams } from "common/utils/exportValidation";

export const syncUsers = (setUsers, newUsers) => {
  setUsers((currentUsers) => [
    ...currentUsers,
    ...newUsers.filter(
      (newUser) =>
        !currentUsers.map((currentUser) => currentUser.id).includes(newUser.id)
    )
  ]);
};
export default function ExcelExportModal({
  open,
  handleClose,
  defaultCompany,
  companies = [],
  initialUsers,
  initialTeams,
  defaultMinDate = null,
  defaultMaxDate = null,
  getUsersSinceDate
}) {
  const api = useApi();
  const alerts = useSnackbarAlerts();
  const { trackLink } = useMatomo();
  const { addExport } = useExportsContext();
  const [minDate, setMinDate] = React.useState(defaultMinDate);
  const [maxDate, setMaxDate] = React.useState(defaultMaxDate);

  const [selectedCompany, setSelectedCompany] = React.useState(defaultCompany);
  const [users, setUsers] = React.useState(initialUsers);
  const [teams, setTeams] = React.useState(initialTeams);

  const [isEnabledDownload, setIsEnabledDownload] = React.useState(true);
  const [consolidatedFile, setConsolidatedFile] = React.useState(true);
  const [controlFormat, setControlFormat] = React.useState(false);
  const [exportValidation, setExportValidation] = React.useState(null);

  React.useEffect(() => {
    const load = async () => {
      if (minDate < defaultMinDate) {
        const newUsers = await getUsersSinceDate(minDate);
        syncUsers(setUsers, newUsers);
      }
    };
    load();
  }, [minDate]);

  React.useEffect(
    () => setIsEnabledDownload(true),
    [minDate, maxDate, users, selectedCompany]
  );

  const today = new Date();

  React.useEffect(() => {
    setMinDate(defaultMinDate);
    setMaxDate(defaultMaxDate);
    setConsolidatedFile(true);
    setControlFormat(false);
  }, [open]);

  const handleUserFilterChange = (newUsers) => {
    const unselectedTeams = unselectAndGetAllTeams(teams);
    setTeams(unselectedTeams);
    setUsers(newUsers);
  };

  const handleTeamFilterChange = (newTeams) => {
    const usersToSelect = getUsersToSelectFromTeamSelection(newTeams, users);
    setUsers(usersToSelect);
    setTeams(newTeams);
  };

  React.useEffect(() => {
    if (!selectedCompany || !minDate || !maxDate) {
      setExportValidation(null);
      return;
    }

    const selectedUsers = users.filter((u) => u.selected);
    const numUsers = selectedUsers.length === 0 ? users.length : selectedUsers.length;

    const validation = validateExportParams({
      numUsers: numUsers,
      minDate: minDate,
      maxDate: maxDate
    });
    
    setExportValidation(validation);
  }, [minDate, maxDate, users, selectedCompany]);

  const classes = modalStyles();

  return (
    <Modal
      open={open}
      handleClose={handleClose}
      title={
        <Typography variant="h2" component="h1" mt={2} className={classes.subtitle}>
          Télécharger le rapport d'activité
        </Typography>
      }
      size="lg"
      content={
        <>
          <Typography gutterBottom>
            Le rapport d'activité est un fichier Excel (.xlsx) qui contient les
            onglets suivants :
          </Typography>
          <ul>
            <li>
              <Typography>
                <strong>Activité</strong>, qui contient la liste des journées de
                travail avec toutes leurs informations : nom de la mission,
                véhicule utilisé, horaires et durées cumulées des activités de
                la journée, frais, ... . Cet onglet est presque identique à la
                vue <strong>Jour</strong> du tableau ci-derrière.
              </Typography>
            </li>
            <li>
              <Typography>
                <strong>Détails</strong>, qui contient l'historique des
                enregistrements et des modifications d'activité.
              </Typography>
            </li>
          </ul>
          <Checkbox
            className={classes.subtitle}
            options={[
              {
                label: "Format conforme pour un contrôle par un agent de contrôle habilité",
                hintText: "Cochez cette case pour générer un export dans le format attendu par les agents de contrôle.",
                nativeInputProps: {
                  checked: controlFormat,
                  onChange: (e) => setControlFormat(e.target.checked)
                }
              }
            ]}
          />
          {controlFormat && (
            <Notice
              type="warning"
              description="Veillez à ne pas modifier la mise en page du fichier avant envoi aux agents de contrôle."
            />
          )}
          <Typography component="h2" variant="h6" className={classes.subtitle}>
            Option d'export
          </Typography>

          <Grid spacing={4} container>
            {companies.length > 1 && (
              <Grid item sm={6} className={classes.flexGrow}>
                <Autocomplete
                  size="small"
                  label="Entreprise"
                  options={companies}
                  defaultValue={defaultCompany}
                  getOptionLabel={(c) => c.name}
                  disableClearable
                  onChange={(_, newValue) => setSelectedCompany(newValue)}
                  renderInput={(params) => (
                    <TextField {...params} label="Entreprise" />
                  )}
                />
              </Grid>
            )}
            {teams?.length > 0 && (
              <Grid
                item
                sm={companies.length > 1 ? 6 : 12}
              >
                <TeamFilter teams={teams} setTeams={handleTeamFilterChange} />
              </Grid>
            )}
            <Grid
              item
              sm={companies.length > 1 ? 6 : 12}
            >
              <EmployeeFilter users={users} setUsers={handleUserFilterChange} />
            </Grid>
            <DateOrDateTimeRangeSelectionContext
              start={minDate}
              setStart={setMinDate}
              end={maxDate}
              setEnd={setMaxDate}
              nullableBounds
            >
              <Grid item sm={6}>
                <MobileDatePicker
                  label="Date de début"
                  value={minDate}
                  inputFormat="d MMMM yyyy"
                  onChange={setMinDate}
                  clearable
                  cancelText={null}
                  clearText="Annuler"
                  disableCloseOnSelect={false}
                  disableMaskedInput={true}
                  maxDate={today}
                  renderInput={(props) => (
                    <TextField {...props} variant="outlined" />
                  )}
                  slotProps={{
                    textField: {
                      size: "small"
                    }
                  }}
                />
              </Grid>
              <Grid item sm={6}>
                <MobileDatePicker
                  label="Date de fin"
                  value={maxDate}
                  inputFormat="d MMMM yyyy"
                  onChange={setMaxDate}
                  clearable
                  cancelText={null}
                  clearText="Annuler"
                  disableCloseOnSelect={false}
                  disableMaskedInput={true}
                  maxDate={today}
                  renderInput={(props) => (
                    <TextField {...props} variant="outlined" />
                  )}
                  slotProps={{
                    textField: {
                      size: "small"
                    }
                  }}
                />
              </Grid>
            </DateOrDateTimeRangeSelectionContext>
          </Grid>

          {exportValidation?.message && (
            <Notice
              type="info"
              description={exportValidation.message}
              className={classes.subtitle}
            />
          )}
          
          {exportValidation?.canChooseConsolidated && (
            <Checkbox
              className={classes.subtitle}
              options={[
                {
                  label: "Télécharger un fichier unique avec les données de l'ensemble de mes salariés",
                  nativeInputProps: {
                    checked: consolidatedFile,
                    onChange: (e) => setConsolidatedFile(e.target.checked)
                  }
                }
              ]}
            />
          )}
        </>
      }
      actions={
        <>
          <LoadingButton
            disabled={!isEnabledDownload}
            onClick={async (e) =>
              await alerts.withApiErrorHandling(async () => {
                let selectedUsers = users.filter((u) => u.selected);
                const options = {
                  company_ids: [selectedCompany.id],
                  one_file_by_employee: !consolidatedFile,
                  control_format: controlFormat
                };
                if (selectedUsers.length > 0)
                  options["user_ids"] = selectedUsers.map((u) => u.id);
                if (minDate) options["min_date"] = isoFormatLocalDate(minDate);
                if (maxDate) options["max_date"] = isoFormatLocalDate(maxDate);
                e.preventDefault();
                trackLink({
                  href: `/download_company_activity_report`,
                  linkType: "download"
                });
                await api.jsonHttpQuery(HTTP_QUERIES.excelExport, {
                  json: options
                });
                setIsEnabledDownload(false);
                handleClose();
                await addExport();
              }, "download-company-report")
            }
          >
            Télécharger
          </LoadingButton>
        </>
      }
    />
  );
}
